import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, fmtMXN, fmtDate, fmtTime, elapsedMin } from "@/lib/api";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import OrderStateBadge from "@/components/OrderStateBadge";
import MapPreview, { googleMapsUrl } from "@/components/MapPreview";
import AgregarItemsPanel from "@/components/AgregarItemsPanel";
import { ArrowLeft, Clock, Phone, MapPin, User, Bike, Send, Trash2, SplitSquareHorizontal, History, Package, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function OrdenDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orden, setOrden] = useState(null);
  const [domicilioOpen, setDomicilioOpen] = useState(false);
  const [domForm, setDomForm] = useState({});
  const [cancelOpen, setCancelOpen] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [quitarTarget, setQuitarTarget] = useState(null);
  const [quitando, setQuitando] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [splitName, setSplitName] = useState("");
  const [splitItemIds, setSplitItemIds] = useState([]);
  const [auditoria, setAuditoria] = useState([]);

  // Edición administrativa de pedidos públicos (solo ADMIN_SUCURSAL, tipo=PUBLICO)
  const [editDetalles, setEditDetalles] = useState(false);
  const [editPago, setEditPago] = useState(false);
  const [formDetalles, setFormDetalles] = useState({});
  const [formPago, setFormPago] = useState({});
  const [savingDetalles, setSavingDetalles] = useState(false);
  const [savingPago, setSavingPago] = useState(false);

  const load = async () => {
    try { const r = await api.get(`/ordenes/${id}`); setOrden(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const loadAudit = async () => {
    try { const r = await api.get(`/ordenes/${id}/auditoria`); setAuditoria(r.data || []); }
    catch {}
  };
  useEffect(() => { load(); loadAudit(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [id]);
  useRealtimeEvents(
    (msg) => {
      if (msg?.type?.startsWith("orden.") && (msg.payload?.orden_id === id || !msg.payload?.orden_id)) {
        load(); loadAudit();
      }
    },
    () => { load(); loadAudit(); },
  );

  if (!orden) return <div className="text-muted-foreground">Cargando…</div>;

  const isMesero = user.role === "MESERO";
  const isAreaUser = ["COCINA", "BEBIDAS", "FREIDORA"].includes(user.role);
  // Corrección manual de datos en pedidos PUBLICO y LLEVAR.
  // ADMIN_SUCURSAL: ambos tipos. MESERO: solo LLEVAR. Resto: no.
  const canEditPublico = (
    (user.role === "ADMIN_SUCURSAL" && ["PUBLICO", "LLEVAR"].includes(orden.tipo)) ||
    (user.role === "MESERO" && orden.tipo === "LLEVAR")
  );

  const startEditDetalles = () => {
    setFormDetalles({
      cliente_nombre: orden.cliente_nombre || "",
      cliente_telefono: orden.cliente_telefono || "",
      cliente_direccion: orden.cliente_direccion || "",
      referencias: orden.referencias || "",
    });
    setEditDetalles(true);
  };
  const startEditPago = () => {
    setFormPago({
      metodo_pago_solicitado: orden.metodo_pago_solicitado || "EFECTIVO",
      monto_con_que_paga: orden.monto_con_que_paga ?? "",
      cambio_requerido: orden.cambio_requerido ?? "",
    });
    setEditPago(true);
  };
  const guardarDetalles = async () => {
    setSavingDetalles(true);
    try {
      const r = await api.patch(`/ordenes/${orden.id}/datos-publico`, {
        cliente_nombre: formDetalles.cliente_nombre,
        cliente_telefono: formDetalles.cliente_telefono,
        cliente_direccion: formDetalles.cliente_direccion,
        referencias: formDetalles.referencias,
      });
      setOrden(r.data);
      setEditDetalles(false);
      loadAudit();
      toast.success("Datos del cliente actualizados");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingDetalles(false); }
  };
  const guardarPago = async () => {
    setSavingPago(true);
    try {
      const payload = {
        metodo_pago_solicitado: formPago.metodo_pago_solicitado,
      };
      // Solo enviar los numéricos si tienen valor; cadena vacía => null
      payload.monto_con_que_paga = formPago.monto_con_que_paga === "" ? null : Number(formPago.monto_con_que_paga);
      payload.cambio_requerido = formPago.cambio_requerido === "" ? null : Number(formPago.cambio_requerido);
      const r = await api.patch(`/ordenes/${orden.id}/datos-publico`, payload);
      setOrden(r.data);
      setEditPago(false);
      loadAudit();
      toast.success("Pago solicitado actualizado");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingPago(false); }
  };

  // Recalculo automático del cambio cuando el admin edita "paga con"
  const onChangePagaCon = (val) => {
    const num = val === "" ? "" : Number(val);
    const total = orden.total || 0;
    setFormPago((s) => ({
      ...s,
      monto_con_que_paga: val,
      cambio_requerido: num === "" ? "" : Math.max(0, +(num - total).toFixed(2)),
    }));
  };

  const canAddItems = !orden.cobrada && orden.estado !== "CANCELADA" && ["MESERO", "ADMIN_SUCURSAL", "CAJA"].includes(user.role);
  const canSplit = orden.tipo === "MESA" && !orden.cobrada && orden.estado !== "CANCELADA" && ["MESERO", "ADMIN_SUCURSAL", "CAJA"].includes(user.role);
  const pendingToSend = (orden.items || []).filter((i) => i.enviado === false && i.estado === "PENDIENTE").length;
  const cuentas = orden.cuentas || [];
  const itemsAsignadosACuenta = new Set(cuentas.flatMap((c) => c.item_ids || []));
  const itemsSinCuenta = (orden.items || []).filter(
    (i) => !itemsAsignadosACuenta.has(i.id) && i.estado !== "CANCELADO" && i.estado !== "DEVUELTO",
  );
  const itemsDisponiblesParaSplit = (orden.items || []).filter(
    (i) => !itemsAsignadosACuenta.has(i.id) && i.estado !== "CANCELADO" && i.estado !== "DEVUELTO" && !i.es_acompanamiento && !i.es_ingrediente_externo,
  );

  const updateItem = async (item, nuevoEstado) => {
    try {
      const r = await api.patch(`/ordenes/${orden.id}/items/${item.id}/estado`, { estado: nuevoEstado });
      setOrden(r.data);
      toast.success("Estado actualizado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const quitarItem = async (item) => {
    setQuitarTarget(item);
  };

  const confirmarQuitar = async () => {
    if (!quitarTarget) return;
    setQuitando(true);
    try {
      const r = await api.delete(`/ordenes/${orden.id}/items/${quitarTarget.id}`);
      setOrden(r.data);
      toast.success(`"${quitarTarget.nombre}" quitado de la orden`);
      setQuitarTarget(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setQuitando(false); }
  };

  const cambiarADomicilio = async () => {
    if (!domForm.cliente_telefono || !domForm.cliente_direccion) return toast.error("Teléfono y dirección requeridos");
    try {
      await api.post(`/ordenes/${orden.id}/cambiar-a-domicilio`, domForm);
      toast.success("Convertida a domicilio");
      setDomicilioOpen(false); setDomForm({}); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancelar = async () => {
    if (!motivo) return toast.error("Motivo requerido");
    try {
      await api.post(`/ordenes/${orden.id}/cancelar`, { motivo });
      toast.success("Orden cancelada");
      setCancelOpen(false); setMotivo(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ============ CUENTAS SEPARADAS ============
  const abrirSplit = () => {
    setSplitName("");
    setSplitItemIds([]);
    setSplitOpen(true);
  };
  const crearCuenta = async () => {
    if (splitItemIds.length === 0) return toast.error("Selecciona al menos un producto para la nueva cuenta");
    try {
      const r = await api.post(`/ordenes/${orden.id}/cuentas`, {
        nombre: splitName.trim() || null,
        item_ids: splitItemIds,
      });
      setOrden(r.data);
      setSplitOpen(false);
      setSplitItemIds([]); setSplitName("");
      toast.success("Cuenta separada creada");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const eliminarCuenta = async (cuentaId) => {
    if (!confirm("¿Eliminar esta cuenta separada?")) return;
    try {
      const r = await api.delete(`/ordenes/${orden.id}/cuentas/${cuentaId}`);
      setOrden(r.data);
      toast.success("Cuenta eliminada");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ============ LISTO PARA REPARTO ============
  const marcarListoReparto = async () => {
    try {
      await api.post(`/ordenes/${orden.id}/marcar-listo-reparto`);
      toast.success("Pedido marcado como listo para reparto");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const nombreItem = (id) => orden.items.find((i) => i.id === id);
  const isDomicilio = orden.entrega_tipo === "DOMICILIO";
  const puedeMarcarListo = isDomicilio
    && ["MESERO", "ADMIN_SUCURSAL", "CAJA"].includes(user.role)
    && !["LISTO_PARA_REPARTO", "ASIGNADO", "EN_CAMINO", "ENTREGADO"].includes(orden.estado_reparto);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft className="h-5 w-5" /></Button>
          <div>
            <h2 className="text-3xl font-black tracking-tight">{orden.numero}</h2>
            <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
              <Clock className="h-3 w-3" /> {fmtDate(orden.created_at)} · hace {elapsedMin(orden.created_at)} min
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <OrderStateBadge state={orden.estado} className="text-sm px-3 py-1.5" />
          {orden.cobrada && <span className="state-COBRADA px-3 py-1.5 text-xs uppercase tracking-wider rounded-md font-bold">Pagada</span>}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-bold mb-4">Productos</h3>
              <div className="space-y-3">
                {orden.items.filter((it) => !it.es_acompanamiento && !it.es_ingrediente_externo).map((it) => {
                  const acomps = orden.items.filter((c) => c.parent_item_id === it.id && c.es_acompanamiento);
                  const ingExternos = orden.items.filter((c) => c.parent_item_id === it.id && c.es_ingrediente_externo);
                  return (
                  <div key={it.id} className={`border-b border-border pb-3 last:border-0 ${it.enviado === false ? "bg-amber-50 dark:bg-amber-950/30 -mx-2 px-2 rounded" : ""}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold">{it.cantidad}× {it.nombre}</span>
                        <span className="text-xs px-2 py-0.5 rounded bg-secondary uppercase tracking-wider">{it.area}</span>
                        {it.enviado === false ? (
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100 font-bold">Pendiente de enviar</span>
                        ) : (
                          <OrderStateBadge state={it.estado} />
                        )}
                      </div>
                      {it.variantes?.length > 0 && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {it.variantes.map((v) => `${v.grupo}: ${v.opcion}`).join(" · ")}
                        </div>
                      )}
                      {it.variantes_rapidas?.length > 0 && (
                        <div className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                          ⚡ Opciones rápidas: {it.variantes_rapidas.join(" · ")}
                        </div>
                      )}
                      {it.observaciones && <div className="text-xs text-orange-500 mt-1">📝 {it.observaciones}</div>}
                      <div className="mt-2 flex gap-2 flex-wrap">
                        {/* Quitar item PENDIENTE_DE_ENVIAR (aún no enviado a cocina) */}
                        {it.enviado === false && ["MESERO", "ADMIN_SUCURSAL", "CAJA"].includes(user.role) && !orden.cobrada && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive border-destructive/40 hover:bg-destructive/10"
                            onClick={() => quitarItem(it)}
                            data-testid={`item-quitar-${it.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" />
                            Quitar
                          </Button>
                        )}
                        {/* Acciones por rol */}
                        {isAreaUser && it.area === user.role && (
                          <>
                            {it.estado === "PENDIENTE" && (
                              <Button size="sm" variant="outline" onClick={() => updateItem(it, "EN_PREPARACION")} data-testid={`item-prep-${it.id}`}>Iniciar preparación</Button>
                            )}
                            {it.estado === "EN_PREPARACION" && (
                              <Button size="sm" onClick={() => updateItem(it, "PREPARADO")} data-testid={`item-listo-${it.id}`}>Marcar preparado</Button>
                            )}
                          </>
                        )}
                        {(isMesero || ["ADMIN_SUCURSAL", "CAJA"].includes(user.role)) && it.estado === "PREPARADO" && (
                          <Button size="sm" onClick={() => updateItem(it, "ENTREGADO")} data-testid={`item-entregado-${it.id}`}>Entregado</Button>
                        )}
                        {["ADMIN_SUCURSAL", "CAJA"].includes(user.role) && !["CANCELADO", "DEVUELTO", "ENTREGADO"].includes(it.estado) && !orden.cobrada && (
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => updateItem(it, "CANCELADO")}>Cancelar item</Button>
                        )}
                      </div>
                    </div>
                    <div className="text-right ml-3">
                      <div className="font-bold">{fmtMXN(it.subtotal)}</div>
                      <div className="text-xs text-muted-foreground">{fmtMXN(it.precio_unitario)} c/u</div>
                    </div>
                  </div>
                  {/* Porciones incluidas (acompañamientos sin costo) */}
                  {acomps.length > 0 && (
                    <div className="mt-2 ml-4 pl-3 border-l-2 border-amber-400 dark:border-amber-600 space-y-2">
                      {acomps.map((c) => (
                        <div key={c.id} className="text-sm" data-testid={`acomp-row-${c.id}`}>
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-200 dark:bg-amber-900/60 text-amber-900 dark:text-amber-100">
                                🍟 PORCIÓN
                              </span>
                              <span className="font-semibold">{c.cantidad}× {c.nombre}</span>
                            </div>
                            <div className={`text-xs font-bold ${(c.precio_unitario && c.precio_unitario > 0) ? "text-primary" : "text-emerald-600 dark:text-emerald-400"}`} data-testid={`acomp-precio-${c.id}`}>
                              {(c.precio_unitario && c.precio_unitario > 0)
                                ? `+${fmtMXN(c.subtotal || (c.precio_unitario * c.cantidad))}`
                                : "Gratis"}
                            </div>
                          </div>
                          <div className="mt-1 flex items-center gap-4 text-xs flex-wrap">
                            <div>
                              <span className="text-muted-foreground">Área:</span>{" "}
                              <span className="font-bold uppercase tracking-wider">{c.area}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-muted-foreground">Estado:</span>
                              {c.enviado === false ? (
                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100 font-bold">Sin enviar</span>
                              ) : (
                                <OrderStateBadge state={c.estado} />
                              )}
                            </div>
                            {/* Fallback granular: marcar porción como entregada si por alguna razón
                                no se hizo cascada al entregar el principal */}
                            {c.estado === "PREPARADO" && (isMesero || ["ADMIN_SUCURSAL", "CAJA"].includes(user.role)) && (
                              <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] ml-auto" onClick={() => updateItem(c, "ENTREGADO")} data-testid={`acomp-entregar-${c.id}`}>
                                Entregar porción
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Ingredientes con preparación externa (subtareas internas por área) */}
                  {ingExternos.length > 0 && (
                    <div className="mt-2 ml-4 pl-3 border-l-2 border-indigo-400 dark:border-indigo-600 space-y-2" data-testid={`ing-externos-of-${it.id}`}>
                      {ingExternos.map((c) => (
                        <div key={c.id} className="text-sm" data-testid={`ing-row-${c.id}`}>
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-indigo-200 dark:bg-indigo-900/60 text-indigo-900 dark:text-indigo-100">
                                🔗 INGREDIENTE
                              </span>
                              <span className="font-semibold">{c.cantidad}× {c.nombre}</span>
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">interno</div>
                          </div>
                          <div className="mt-1 flex items-center gap-4 text-xs flex-wrap">
                            <div>
                              <span className="text-muted-foreground">Área:</span>{" "}
                              <span className="font-bold uppercase tracking-wider">{c.area}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-muted-foreground">Estado:</span>
                              {c.enviado === false ? (
                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100 font-bold">Sin enviar</span>
                              ) : (
                                <OrderStateBadge state={c.estado} />
                              )}
                            </div>
                            {c.observaciones && (
                              <div className="text-xs text-orange-500 italic">📝 {c.observaciones}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  </div>
                  );
                })}
              </div>
              <div className="mt-4 pt-4 border-t border-border flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Subtotal</span>
                <span className="font-semibold">{fmtMXN(orden.subtotal)}</span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-base font-bold">Total</span>
                <span className="text-2xl font-black text-primary">{fmtMXN(orden.total)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Agregar productos a la orden */}
          {canAddItems && (
            <AgregarItemsPanel
              ordenId={orden.id}
              pendingItemsCount={pendingToSend}
              onChanged={load}
            />
          )}

          {/* Acciones de orden */}
          {!orden.cobrada && ["ADMIN_SUCURSAL", "CAJA", "MESERO"].includes(user.role) && (
            <Card>
              <CardContent className="p-6 flex flex-wrap gap-2">
                {canSplit && (
                  <Button variant="outline" onClick={abrirSplit} data-testid="separar-cuenta-btn">
                    <SplitSquareHorizontal className="h-4 w-4 mr-2" />Separar cuenta
                  </Button>
                )}
                {puedeMarcarListo && (
                  <Button variant="outline" onClick={marcarListoReparto} data-testid="marcar-listo-reparto-btn">
                    <Package className="h-4 w-4 mr-2" />Marcar empaquetado / Listo para reparto
                  </Button>
                )}
                {user.role === "MESERO" && orden.tipo === "LLEVAR" && orden.entrega_tipo !== "DOMICILIO" && (
                  <Button variant="outline" onClick={() => setDomicilioOpen(true)} data-testid="cambiar-domicilio-btn">Cambiar a domicilio</Button>
                )}
                {["ADMIN_SUCURSAL", "CAJA"].includes(user.role) && !["CANCELADA", "DEVUELTA"].includes(orden.estado) && (
                  <Button variant="destructive" onClick={() => setCancelOpen(true)} data-testid="cancelar-orden-btn">
                    {orden.cobrada ? "Cancelar y reembolsar" : "Cancelar orden"}
                  </Button>
                )}
                {["ADMIN_SUCURSAL", "CAJA"].includes(user.role) && !orden.cobrada && (
                  <Button onClick={() => navigate(`/caja?orden=${orden.id}`)} data-testid="ir-cobrar-btn">Ir a cobrar</Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Cuentas separadas */}
          {cuentas.length > 0 && (
            <Card data-testid="cuentas-separadas-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <SplitSquareHorizontal className="h-5 w-5 text-primary" />Cuentas separadas ({cuentas.length})
                </h3>
                <div className="space-y-3">
                  {cuentas.map((c) => (
                    <div key={c.id} className={`rounded-lg border p-4 ${c.cobrada ? "border-emerald-500/50 bg-emerald-50/40 dark:bg-emerald-950/20" : "border-border"}`} data-testid={`cuenta-${c.id}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="font-bold flex items-center gap-2">
                          {c.nombre}
                          {c.cobrada ? (
                            <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500 text-white uppercase tracking-wider font-bold">Pagada</span>
                          ) : (
                            <span className="text-[10px] px-2 py-0.5 rounded bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100 uppercase tracking-wider font-bold">Pendiente</span>
                          )}
                        </div>
                        <div className="text-xl font-black text-primary">{fmtMXN(c.total || 0)}</div>
                      </div>
                      <ul className="mt-2 space-y-1 text-sm">
                        {(c.item_ids || []).map((iid) => {
                          const it = nombreItem(iid);
                          if (!it) return null;
                          return (
                            <li key={iid} className="flex justify-between text-muted-foreground">
                              <span>{it.cantidad}× {it.nombre}</span>
                              <span>{fmtMXN(it.subtotal)}</span>
                            </li>
                          );
                        })}
                      </ul>
                      <div className="mt-3 flex gap-2 flex-wrap">
                        {!c.cobrada && ["CAJA", "ADMIN_SUCURSAL"].includes(user.role) && (
                          <Button size="sm" onClick={() => navigate(`/caja?orden=${orden.id}&cuenta=${c.id}`)} data-testid={`cobrar-cuenta-${c.id}`}>
                            Cobrar esta cuenta
                          </Button>
                        )}
                        {!c.cobrada && ["MESERO", "ADMIN_SUCURSAL", "CAJA"].includes(user.role) && (
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => eliminarCuenta(c.id)} data-testid={`eliminar-cuenta-${c.id}`}>
                            Eliminar
                          </Button>
                        )}
                        {c.cobrada && c.cobrada_por_nombre && (
                          <div className="text-xs text-muted-foreground">Cobrada por {c.cobrada_por_nombre} · {fmtTime(c.cobrada_en)}</div>
                        )}
                      </div>
                    </div>
                  ))}
                  {itemsSinCuenta.length > 0 && (
                    <div className="text-xs text-muted-foreground italic pt-2 border-t border-border">
                      {itemsSinCuenta.length} producto(s) sin cuenta asignada · {fmtMXN(itemsSinCuenta.reduce((s, i) => s + i.subtotal, 0))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Auditoría */}
          {auditoria.length > 0 && (
            <Card data-testid="auditoria-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <History className="h-5 w-5 text-primary" />Historial
                </h3>
                <ol className="relative border-l-2 border-border ml-2 space-y-3">
                  {auditoria.map((a) => (
                    <HistorialEvento key={a.id} log={a} />
                  ))}
                </ol>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm uppercase tracking-widest text-muted-foreground">Detalles</h3>
                {canEditPublico && !editDetalles && (
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={startEditDetalles} data-testid="edit-detalles-btn" title="Corregir datos">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              {editDetalles ? (
                <div className="space-y-3" data-testid="edit-detalles-form">
                  <div>
                    <Label className="text-xs">Nombre del cliente</Label>
                    <Input value={formDetalles.cliente_nombre} onChange={(e) => setFormDetalles({ ...formDetalles, cliente_nombre: e.target.value })} data-testid="edit-cliente-nombre" />
                  </div>
                  <div>
                    <Label className="text-xs">Teléfono</Label>
                    <Input value={formDetalles.cliente_telefono} onChange={(e) => setFormDetalles({ ...formDetalles, cliente_telefono: e.target.value })} data-testid="edit-cliente-telefono" />
                  </div>
                  <div>
                    <Label className="text-xs">Dirección</Label>
                    <Textarea value={formDetalles.cliente_direccion} onChange={(e) => setFormDetalles({ ...formDetalles, cliente_direccion: e.target.value })} data-testid="edit-cliente-direccion" />
                  </div>
                  <div>
                    <Label className="text-xs">Referencia</Label>
                    <Input value={formDetalles.referencias} onChange={(e) => setFormDetalles({ ...formDetalles, referencias: e.target.value })} data-testid="edit-cliente-referencias" />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" onClick={guardarDetalles} disabled={savingDetalles} data-testid="save-detalles-btn">
                      <Check className="h-4 w-4 mr-1" />Guardar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditDetalles(false)} disabled={savingDetalles} data-testid="cancel-detalles-btn">
                      <X className="h-4 w-4 mr-1" />Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 text-sm">
                  <div><span className="text-muted-foreground">Tipo:</span> <span className="font-bold">{orden.tipo}</span></div>
                  {orden.mesa_nombre && <div className="flex items-center gap-2"><User className="h-3 w-3 text-muted-foreground" /> {orden.mesa_nombre}</div>}
                  {orden.cliente_nombre && <div className="flex items-center gap-2"><User className="h-3 w-3 text-muted-foreground" /> {orden.cliente_nombre}</div>}
                  {orden.cliente_telefono && <div className="flex items-center gap-2"><Phone className="h-3 w-3 text-muted-foreground" /> {orden.cliente_telefono}</div>}
                  {orden.cliente_direccion && <div className="flex items-start gap-2"><MapPin className="h-3 w-3 text-muted-foreground mt-1" /> <span>{orden.cliente_direccion}</span></div>}
                  {orden.direccion_origen === "MANUAL" && <div className="text-xs text-amber-600 italic">Dirección capturada manualmente por sucursal</div>}
                  {orden.referencias && <div className="text-xs text-muted-foreground">Ref: {orden.referencias}</div>}
                  {orden.observaciones && <div className="text-xs text-muted-foreground">📝 {orden.observaciones}</div>}
                  {(orden.created_by_nombre || orden.created_by_role) && (
                    <div className="pt-2 mt-2 border-t border-border text-xs">
                      <span className="text-muted-foreground">Registrado por:</span>{" "}
                      <span className="font-semibold">{orden.created_by_nombre || "Cliente web"}</span>
                      {orden.created_by_role && <span className="ml-1 text-[10px] uppercase tracking-wider text-muted-foreground">/ {orden.created_by_role}</span>}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {typeof orden.latitud === "number" && typeof orden.longitud === "number" && (
            <Card data-testid="ubicacion-card">
              <CardContent className="p-5">
                <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> Ubicación GPS
                </h3>
                <MapPreview lat={orden.latitud} lng={orden.longitud} height={200} readOnly />
                <div className="mt-3 text-xs text-muted-foreground">
                  Coordenadas: <span className="font-mono">{orden.latitud.toFixed(6)}, {orden.longitud.toFixed(6)}</span>
                </div>
                {orden.ubicacion_capturada_en && (
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Capturada: {new Date(orden.ubicacion_capturada_en).toLocaleString()}
                  </div>
                )}
                <a
                  href={googleMapsUrl(orden.latitud, orden.longitud)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex w-full justify-center items-center gap-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11 px-4 transition-colors"
                  data-testid="open-google-maps-btn"
                >
                  <MapPin className="h-4 w-4" />
                  Abrir en Google Maps
                </a>
              </CardContent>
            </Card>
          )}

          {orden.estado_reparto && (
            <Card>
              <CardContent className="p-5">
                <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2"><Bike className="h-4 w-4" /> Reparto</h3>
                <div className="space-y-2 text-sm">
                  <div><span className="text-muted-foreground">Estado:</span> <OrderStateBadge state={orden.estado_reparto} /></div>
                  {orden.repartidor_nombre && <div>Repartidor: <span className="font-semibold">{orden.repartidor_nombre}</span></div>}
                  {orden.comentario_reparto && <div className="text-xs text-muted-foreground">{orden.comentario_reparto}</div>}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Pago solicitado desde web (antes del cobro real). Útil para Caja y Repartidor.
              Aplica para PUBLICO o LLEVAR a domicilio. */}
          {((orden.metodo_pago_solicitado && !orden.cobrada) || (canEditPublico && !orden.cobrada && orden.entrega_tipo === "DOMICILIO")) && (
            <Card data-testid="pago-solicitado-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm uppercase tracking-widest text-muted-foreground">Pago solicitado</h3>
                  {canEditPublico && !editPago && (
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={startEditPago} data-testid="edit-pago-btn" title="Corregir pago solicitado">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                {editPago ? (
                  <div className="space-y-3" data-testid="edit-pago-form">
                    <div>
                      <Label className="text-xs">Método de pago</Label>
                      <select
                        value={formPago.metodo_pago_solicitado}
                        onChange={(e) => setFormPago({ ...formPago, metodo_pago_solicitado: e.target.value })}
                        className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary"
                        data-testid="edit-metodo-pago"
                      >
                        <option value="EFECTIVO">EFECTIVO</option>
                        <option value="TARJETA">TARJETA</option>
                        <option value="TRANSFERENCIA">TRANSFERENCIA</option>
                      </select>
                    </div>
                    {formPago.metodo_pago_solicitado === "EFECTIVO" && (
                      <>
                        <div>
                          <Label className="text-xs">Paga con</Label>
                          <Input type="number" inputMode="decimal" min="0" step="0.01" value={formPago.monto_con_que_paga} onChange={(e) => onChangePagaCon(e.target.value)} data-testid="edit-paga-con" />
                        </div>
                        <div>
                          <Label className="text-xs">Cambio a llevar</Label>
                          <Input type="number" inputMode="decimal" min="0" step="0.01" value={formPago.cambio_requerido} onChange={(e) => setFormPago({ ...formPago, cambio_requerido: e.target.value })} data-testid="edit-cambio" />
                        </div>
                      </>
                    )}
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" onClick={guardarPago} disabled={savingPago} data-testid="save-pago-btn">
                        <Check className="h-4 w-4 mr-1" />Guardar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditPago(false)} disabled={savingPago} data-testid="cancel-pago-btn">
                        <X className="h-4 w-4 mr-1" />Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1 text-sm">
                    <div>Método: <span className="font-bold" data-testid="pago-solicitado-metodo">{orden.metodo_pago_solicitado || "—"}</span></div>
                    {orden.monto_con_que_paga !== undefined && orden.monto_con_que_paga !== null && (
                      <>
                        <div>Paga con: <span className="font-bold">{fmtMXN(orden.monto_con_que_paga)}</span></div>
                        <div>
                          Cambio a llevar:{" "}
                          <span className="font-black text-emerald-600 dark:text-emerald-400" data-testid="pago-solicitado-cambio">
                            {fmtMXN(orden.cambio_requerido ?? Math.max(0, (orden.monto_con_que_paga || 0) - (orden.total || 0)))}
                          </span>
                        </div>
                      </>
                    )}
                    <div className="text-xs text-muted-foreground italic">Indicado por el cliente desde el pedido web.</div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {orden.cobrada && (
            <Card>
              <CardContent className="p-5">
                <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Pago</h3>
                <div className="space-y-1 text-sm">
                  <div>Método: <span className="font-bold">{orden.metodo_pago}</span></div>
                  <div>Total: <span className="font-bold">{fmtMXN(orden.total)}</span></div>
                  <div>Recibido: {fmtMXN(orden.monto_recibido)}</div>
                  <div>Cambio: {fmtMXN(orden.cambio)}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(orden.cobrada_en)}</div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={domicilioOpen} onOpenChange={setDomicilioOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cambiar a entrega a domicilio</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Teléfono *</Label><Input value={domForm.cliente_telefono || ""} onChange={(e) => setDomForm({ ...domForm, cliente_telefono: e.target.value })} /></div>
            <div><Label>Dirección *</Label><Textarea value={domForm.cliente_direccion || ""} onChange={(e) => setDomForm({ ...domForm, cliente_direccion: e.target.value })} /></div>
            <div><Label>Referencias</Label><Input value={domForm.referencias || ""} onChange={(e) => setDomForm({ ...domForm, referencias: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDomicilioOpen(false)}>Cancelar</Button><Button onClick={cambiarADomicilio}>Convertir a domicilio</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!quitarTarget} onOpenChange={(o) => !o && setQuitarTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <Trash2 className="h-5 w-5" />
              </span>
              Quitar producto
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-base">
              ¿Deseas quitar <span className="font-bold">"{quitarTarget?.nombre}"</span> de la orden?
            </p>
            <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
              Este producto aún no ha sido enviado a preparación, por lo que se eliminará de la cuenta.
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setQuitarTarget(null)}
              disabled={quitando}
              data-testid="quitar-cancelar-btn"
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmarQuitar}
              disabled={quitando}
              data-testid="quitar-confirmar-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {quitando ? "Quitando…" : "Quitar producto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cancelar orden</DialogTitle></DialogHeader>
          <Textarea placeholder="Motivo de cancelación" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          <DialogFooter><Button variant="outline" onClick={() => setCancelOpen(false)}>No</Button><Button variant="destructive" onClick={cancelar}>Sí, cancelar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Separar cuenta */}
      <Dialog open={splitOpen} onOpenChange={setSplitOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
                <SplitSquareHorizontal className="h-5 w-5" />
              </span>
              Separar cuenta
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Nombre de la cuenta (opcional)</Label>
              <Input
                value={splitName}
                onChange={(e) => setSplitName(e.target.value)}
                placeholder="Ej. Juan, Cuenta 1, Mesa A…"
                className="mt-1"
                data-testid="split-name-input"
              />
            </div>
            <div>
              <Label>Productos a asignar</Label>
              <div className="mt-2 space-y-2 max-h-64 overflow-y-auto pr-2">
                {itemsDisponiblesParaSplit.length === 0 ? (
                  <div className="text-sm text-muted-foreground italic py-3">
                    No hay productos disponibles. Todos están asignados a cuentas existentes.
                  </div>
                ) : itemsDisponiblesParaSplit.map((it) => {
                  const checked = splitItemIds.includes(it.id);
                  return (
                    <label key={it.id} className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer hover:bg-secondary/40 ${checked ? "border-primary bg-primary/5" : "border-border"}`}>
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          if (v) setSplitItemIds([...splitItemIds, it.id]);
                          else setSplitItemIds(splitItemIds.filter((x) => x !== it.id));
                        }}
                        data-testid={`split-item-${it.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold">{it.cantidad}× {it.nombre}</div>
                        <div className="text-xs text-muted-foreground">{it.area}</div>
                      </div>
                      <div className="font-bold text-sm">{fmtMXN(it.subtotal)}</div>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="rounded-md border border-border bg-muted/40 p-3 text-sm flex items-center justify-between">
              <span className="text-muted-foreground">Total de la nueva cuenta</span>
              <span className="font-black text-lg text-primary">
                {fmtMXN(itemsDisponiblesParaSplit.filter((i) => splitItemIds.includes(i.id)).reduce((s, i) => s + i.subtotal, 0))}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSplitOpen(false)} data-testid="split-cancelar-btn">Cancelar</Button>
            <Button onClick={crearCuenta} disabled={splitItemIds.length === 0} data-testid="split-confirmar-btn">
              Crear cuenta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============== HISTORIAL: renderer enriquecido ==============
function HistChip({ children, tone = "muted" }) {
  const cls = {
    muted: "bg-muted text-muted-foreground",
    primary: "bg-primary/10 text-primary",
    success: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    warn: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
    danger: "bg-destructive/10 text-destructive",
  }[tone];
  return <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}>{children}</span>;
}

function HistorialEvento({ log }) {
  const d = log.detalle || {};
  const meta = ACCION_META[log.accion] || { label: log.accion, color: "bg-primary", Icon: null };
  const user = log.user_email ? log.user_email.split("@")[0] : "Sistema";

  // Renderizar el cuerpo según acción
  let body = null;
  switch (log.accion) {
    case "CREAR_ORDEN":
      body = (
        <div className="text-xs text-foreground/80 space-y-0.5">
          <div>Orden <span className="font-bold">{d.numero}</span> · Tipo: <HistChip tone="primary">{d.tipo}</HistChip>
            {d.mesa_nombre && <> · <span className="font-bold">{d.mesa_nombre}</span></>}
            {d.cliente_nombre && <> · Cliente: <span className="font-bold">{d.cliente_nombre}</span></>}
          </div>
          <div>Total: <span className="font-bold">{fmtMXN(d.total || 0)}</span> · {d.items_count} {d.items_count === 1 ? "item" : "items"}</div>
          {(d.items_resumen || []).length > 0 && (
            <div className="text-[11px] text-muted-foreground">{(d.items_resumen || []).join(" · ")}</div>
          )}
        </div>
      );
      break;
    case "AGREGAR_ITEMS":
      body = (
        <div className="text-xs">
          <div>{d.count} {d.count === 1 ? "producto agregado" : "productos agregados"}{d.numero ? ` a ${d.numero}` : ""}</div>
          {(d.items_agregados || []).length > 0 && (
            <div className="text-[11px] text-muted-foreground">{(d.items_agregados || []).join(" · ")}</div>
          )}
        </div>
      );
      break;
    case "ENVIAR_COCINA":
      body = (
        <div className="text-xs">
          <div>{d.items_enviados} {d.items_enviados === 1 ? "elemento enviado" : "elementos enviados"} a {(d.areas || []).length === 1 ? "el área" : "las áreas"}: {(d.areas || []).map((a) => <HistChip key={a} tone="primary">{a}</HistChip>).reduce((p, c) => [p, " ", c], null)}</div>
          {(d.items_enviados_detalle || []).length > 0 && (
            <ul className="mt-1 space-y-1">
              {(d.items_enviados_detalle || []).slice(0, 12).map((x, i) => (
                <li key={i} className="text-[11px] leading-snug">
                  <span className="font-bold text-foreground/90">{x.producto}</span>
                  {x.cantidad > 1 && <span className="text-muted-foreground"> ×{x.cantidad}</span>}
                  <span className="text-muted-foreground"> enviado{x.cantidad > 1 ? "s" : ""} a </span>
                  <HistChip tone="primary">{x.area}</HistChip>
                  {x.es_ingrediente_externo && x.parent_producto_nombre && (
                    <div className="ml-3 text-[10px] text-muted-foreground">Pertenece a <span className="font-bold text-foreground/80">{x.parent_producto_nombre}</span></div>
                  )}
                  {x.es_acompanamiento && x.parent_producto_nombre && (
                    <div className="ml-3 text-[10px] text-muted-foreground">Acompaña a <span className="font-bold text-foreground/80">{x.parent_producto_nombre}</span></div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      );
      break;
    case "ACTUALIZAR_ITEM":
      body = (
        <div className="text-xs space-y-1">
          <div className="leading-snug">
            <span className="font-bold text-foreground/90">{d.producto_nombre || "Producto"}</span>
            <span className="text-muted-foreground"> cambió a </span>
            <HistChip tone={d.estado === "ENTREGADO" ? "success" : d.estado === "CANCELADO" ? "danger" : "primary"}>{d.estado}</HistChip>
            {d.estado_anterior && (
              <span className="text-[10px] text-muted-foreground ml-1">(desde {d.estado_anterior})</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
            {d.area && <span>Área: <HistChip tone="primary">{d.area}</HistChip></span>}
            {d.es_acompanamiento && d.parent_producto_nombre && (
              <span>Acompaña a <span className="font-bold text-foreground/80">{d.parent_producto_nombre}</span></span>
            )}
            {d.es_ingrediente_externo && d.parent_producto_nombre && (
              <span>Pertenece a <span className="font-bold text-foreground/80">{d.parent_producto_nombre}</span></span>
            )}
          </div>
          {Array.isArray(d.cascadeados_detalle) && d.cascadeados_detalle.length > 0 && (
            <div className="mt-1 ml-2 pl-2 border-l-2 border-muted text-[11px] text-muted-foreground">
              <div className="italic">Cascadeó a {d.cascadeados_detalle.length} sub-tarea(s):</div>
              <ul className="space-y-0.5 mt-0.5">
                {d.cascadeados_detalle.map((c, i) => (
                  <li key={i}>
                    <span className="font-bold text-foreground/80">{c.nombre}</span>
                    {" cambió a "}<HistChip tone={d.estado === "ENTREGADO" ? "success" : "danger"}>{d.estado}</HistChip>
                    {c.area && <> · Área: <HistChip tone="primary">{c.area}</HistChip></>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!d.cascadeados_detalle && !!d.cascadeados && (
            <div className="text-[10px] text-muted-foreground italic">+{d.cascadeados} sub-tarea(s) actualizada(s) en cascada</div>
          )}
        </div>
      );
      break;
    case "QUITAR_ITEM":
      body = (
        <div className="text-xs">
          <div>Quitó <span className="font-bold">{d.producto_nombre || d.producto}</span>{d.area && <> de <HistChip tone="primary">{d.area}</HistChip></>}</div>
          {d.acompanamientos_removidos > 0 && <div className="text-[11px] text-muted-foreground">+{d.acompanamientos_removidos} hijo(s) eliminado(s)</div>}
        </div>
      );
      break;
    case "CANCELAR_ITEM":
      body = (
        <div className="text-xs">
          <div>Canceló <span className="font-bold">{d.producto_nombre || d.producto}</span>{d.area && <> ({d.area})</>}</div>
          {d.motivo && <div className="text-[11px]">Motivo: <span className="italic">{d.motivo}</span></div>}
          {d.items_cancelados_total > 1 && <div className="text-[10px] text-muted-foreground">Incluye {d.items_cancelados_total - 1} hijo(s)</div>}
        </div>
      );
      break;
    case "DEVOLVER_ITEM":
      body = (
        <div className="text-xs">
          <div>Devolvió <span className="font-bold">{d.producto_nombre || d.producto}</span>{d.area && <> ({d.area})</>}</div>
          {d.motivo && <div className="text-[11px]">Motivo: <span className="italic">{d.motivo}</span></div>}
          <div className="text-[10px] text-muted-foreground">Monto: {fmtMXN(d.monto || 0)} · cobrada: {d.cobrada ? "sí" : "no"}</div>
        </div>
      );
      break;
    case "CANCELAR_ORDEN":
      body = (
        <div className="text-xs">
          <div>Orden <span className="font-bold">{d.numero}</span>{d.mesa_nombre && <> · {d.mesa_nombre}</>}{d.estado_anterior && <> · de <span className="font-semibold">{d.estado_anterior}</span> a <span className="font-semibold">CANCELADA</span></>}</div>
          {d.motivo && <div className="text-[11px]">Motivo: <span className="italic">{d.motivo}</span></div>}
        </div>
      );
      break;
    case "REEMBOLSAR_ORDEN":
      body = (
        <div className="text-xs">
          <div>Orden <span className="font-bold">{d.numero}</span>{d.mesa_nombre && <> · {d.mesa_nombre}</>} reembolsada</div>
          <div className="text-[11px]">Monto reembolsado: <span className="font-bold text-emerald-600 dark:text-emerald-400">{fmtMXN(d.monto_reembolsado || d.total || 0)}</span></div>
          {d.motivo && <div className="text-[11px]">Motivo: <span className="italic">{d.motivo}</span></div>}
          {d.estado_anterior && <div className="text-[10px] text-muted-foreground">Estado: {d.estado_anterior} → DEVUELTA</div>}
        </div>
      );
      break;
    case "DEVOLVER_ORDEN":
      body = (
        <div className="text-xs">
          <div>Orden <span className="font-bold">{d.numero}</span>{d.mesa_nombre && <> · {d.mesa_nombre}</>} · Total: {fmtMXN(d.total || 0)}</div>
          {d.motivo && <div className="text-[11px]">Motivo: <span className="italic">{d.motivo}</span></div>}
        </div>
      );
      break;
    case "COBRAR_ORDEN":
      body = (
        <div className="text-xs">
          <div>Orden <span className="font-bold">{d.numero}</span>{d.mesa_nombre && <> · {d.mesa_nombre}</>}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2">
            <HistChip tone="success">{d.metodo}</HistChip>
            <span>Total: <span className="font-bold">{fmtMXN(d.total || 0)}</span></span>
            {d.monto_recibido != null && <span>Recibido: {fmtMXN(d.monto_recibido)}</span>}
            {d.cambio != null && <span>Cambio: {fmtMXN(d.cambio)}</span>}
          </div>
        </div>
      );
      break;
    case "COBRAR_CUENTA":
      body = (
        <div className="text-xs">
          <div>{d.cuenta_nombre || "Cuenta"} · <HistChip tone="success">{d.metodo}</HistChip> · Total: <span className="font-bold">{fmtMXN(d.total || 0)}</span></div>
          {d.cierra_orden && <div className="text-[11px] text-emerald-600 dark:text-emerald-400">Cerró la orden</div>}
        </div>
      );
      break;
    case "LIBERAR_MESA": {
      const motivoLabel = {
        ORDEN_COBRADA_Y_ENTREGADA: "Orden cobrada y entregada",
        ORDEN_CANCELADA: "Orden cancelada",
        LIBERACION_MANUAL: "Liberación manual",
      }[d.motivo] || (d.motivo || "").replace(/_/g, " ");
      body = (
        <div className="text-xs space-y-0.5">
          <div>
            <span className="font-bold">{d.mesa_nombre || "Mesa"}</span> liberada
            {d.tipo === "automatica" ? " automáticamente" : " manualmente"}
            {d.numero && <> · Orden <span className="font-bold">{d.numero}</span></>}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-0.5">
            <span>Motivo: <HistChip tone="primary">{motivoLabel}</HistChip></span>
            <span>Acción ejecutada por: <HistChip tone={d.tipo === "automatica" ? "warn" : "muted"}>{d.tipo === "automatica" ? "SISTEMA" : (user.toUpperCase())}</HistChip></span>
          </div>
          {d.regla && <div className="text-[11px] text-muted-foreground italic">Regla aplicada: {d.regla}</div>}
        </div>
      );
      break;
    }
    case "ASIGNAR_REPARTIDOR":
      body = <div className="text-xs">Repartidor asignado: <span className="font-bold">{d.repartidor || "—"}</span></div>;
      break;
    case "TOMAR_ENVIO":
      body = <div className="text-xs">Repartidor tomó la orden <span className="font-bold">{d.numero}</span></div>;
      break;
    case "MARCAR_LISTO_REPARTO":
      body = <div className="text-xs">Marcada lista para reparto · <span className="font-bold">{d.numero}</span></div>;
      break;
    case "ESTADO_REPARTO":
      body = <div className="text-xs">Estado de reparto: <HistChip tone="primary">{d.estado}</HistChip></div>;
      break;
    case "CAMBIO_A_DOMICILIO":
      body = <div className="text-xs">Cambiada a domicilio · {d.cliente_direccion || ""}</div>;
      break;
    case "CUENTA_CREAR":
      body = <div className="text-xs">Cuenta creada · {d.items || 0} items</div>;
      break;
    case "CUENTA_ELIMINAR":
      body = <div className="text-xs">Cuenta eliminada</div>;
      break;
    case "CUENTA_ACTUALIZAR":
      body = <div className="text-xs">Cuenta actualizada</div>;
      break;
    case "MODIFICAR_DATOS_PUBLICO": {
      const cambios = Array.isArray(d.cambios) ? d.cambios : [];
      const MONEY_FIELDS = new Set(["monto_con_que_paga", "cambio_requerido"]);
      const fmtVal = (campo, val) => {
        if (val === null || val === undefined || val === "") return "—";
        if (MONEY_FIELDS.has(campo)) return fmtMXN(val);
        return String(val);
      };
      body = (
        <div className="text-xs space-y-1">
          {cambios.map((c, i) => (
            <div key={i}>
              Modificó <span className="font-semibold">{c.campo_legible || c.campo}</span> de{" "}
              <span className="line-through text-muted-foreground">{fmtVal(c.campo, c.anterior)}</span>{" "}
              a <span className="font-bold text-foreground">{fmtVal(c.campo, c.nuevo)}</span>
            </div>
          ))}
        </div>
      );
      break;
    }
    default:
      // Fallback genérico para acciones desconocidas o pre-enrichment
      body = Object.keys(d).length > 0 ? (
        <div className="text-xs text-muted-foreground">
          {Object.entries(d).slice(0, 4).map(([k, v]) => (
            <span key={k} className="mr-3">{k}: <span className="text-foreground/80">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span></span>
          ))}
        </div>
      ) : null;
  }

  return (
    <li className="pl-4 -ml-px" data-testid={`historial-${log.accion}`}>
      <div className={`absolute -left-[5px] mt-1 h-2.5 w-2.5 rounded-full ${meta.color}`} />
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{fmtTime(log.created_at)}</div>
      <div className="text-sm font-bold flex items-center gap-2">
        <span>{meta.label}</span>
        <span className="text-[11px] text-muted-foreground font-normal">por <span className="text-primary">{user}</span></span>
      </div>
      {body && <div className="mt-1">{body}</div>}
    </li>
  );
}

// Mapeo acción → etiqueta legible + color del punto del timeline
const ACCION_META = {
  CREAR_ORDEN: { label: "Orden creada", color: "bg-emerald-500" },
  AGREGAR_ITEMS: { label: "Productos agregados", color: "bg-blue-500" },
  ENVIAR_COCINA: { label: "Enviado a preparación", color: "bg-indigo-500" },
  ACTUALIZAR_ITEM: { label: "Cambio de estado", color: "bg-amber-500" },
  QUITAR_ITEM: { label: "Producto quitado", color: "bg-orange-500" },
  CANCELAR_ITEM: { label: "Producto cancelado", color: "bg-red-500" },
  DEVOLVER_ITEM: { label: "Producto devuelto", color: "bg-rose-500" },
  CANCELAR_ORDEN: { label: "Orden cancelada", color: "bg-red-600" },
  DEVOLVER_ORDEN: { label: "Orden devuelta", color: "bg-rose-600" },
  COBRAR_ORDEN: { label: "Orden cobrada", color: "bg-emerald-600" },
  COBRAR_CUENTA: { label: "Cuenta cobrada", color: "bg-emerald-500" },
  LIBERAR_MESA: { label: "Mesa liberada", color: "bg-teal-500" },
  ASIGNAR_REPARTIDOR: { label: "Repartidor asignado", color: "bg-cyan-500" },
  TOMAR_ENVIO: { label: "Envío tomado", color: "bg-cyan-600" },
  MARCAR_LISTO_REPARTO: { label: "Lista para reparto", color: "bg-cyan-500" },
  ESTADO_REPARTO: { label: "Reparto actualizado", color: "bg-cyan-500" },
  CAMBIO_A_DOMICILIO: { label: "Cambio a domicilio", color: "bg-purple-500" },
  CUENTA_CREAR: { label: "Cuenta creada", color: "bg-slate-500" },
  CUENTA_ACTUALIZAR: { label: "Cuenta actualizada", color: "bg-slate-500" },
  CUENTA_ELIMINAR: { label: "Cuenta eliminada", color: "bg-slate-500" },
  REEMBOLSAR_ORDEN: { label: "Orden reembolsada", color: "bg-amber-600" },
  MODIFICAR_DATOS_PUBLICO: { label: "Datos corregidos", color: "bg-purple-500" },
  VARIANTE_RAPIDA: { label: "Variante rápida registrada", color: "bg-violet-400" },
};
